"use client"

import { useEffect, useRef } from "react"
import { ChatInput } from "./ChatInput"
import { ChatMessages } from "./ChatMessages"
import type { FeedbackScope } from "./ChatMessage"
import { Message } from "./types"

import { DEFAULT_AGENT_NAME, type AgentName } from "@/lib/agentcore-client"
import { submitChatFeedback } from "@/services/feedbackService"
import { useAuth } from "@/hooks/useAuth"
import { useDefaultTool, useWidgetToolRenderer } from "@/hooks/useToolRenderer"
import { ToolCallDisplay } from "./ToolCallDisplay"
import { DescribeVariablesRenderer } from "./widgets/DescribeVariablesGrid"
import { useWidgetRenderers } from "./useWidgetRenderers"
import { useChatSessions } from "./ChatSessionsContext"
import { HowToUseDialog } from "./HowToUseDialog"
import { getHowToUseContent } from "./howToUseContent"
import { useInvocationContext } from "./InvocationContext"

/**
 * Per-agent welcome screen copy.
 * Add a new entry here whenever a new AgentName is introduced.
 */
export const AGENT_WELCOME: Record<AgentName, { title: string; subtitle: string }> = {
  "survey-variable-discovery": {
    title: "Survey Variable Discovery",
    subtitle: "Ask me to help you find and understand survey variables across MAP datasets.",
  },
  "deep-literature-review": {
    title: "Deep Literature Review",
    subtitle: "Ask me to search and synthesise the malaria research literature for you.",
  },
  "exploratory-literature-review": {
    title: "Exploratory Literature Review",
    subtitle: "Ask me to conduct systematic literature research across PubMed, EuropePMC, bioRxiv, and 1200+ scientific databases.",
  },
}

type ChatInterfaceProps = {
  /** The session ID to use for this conversation (managed by parent). */
  sessionId: string
  /** Whether this session already exists in the backend (selected from sidebar). */
  isExistingSession: boolean
  /**
   * Called whenever the presence of assistant messages changes.
   * The parent layout uses this to enable/disable the "New Chat" header button.
   *
   * @param hasMessages - `true` when at least one assistant message exists.
   */
  onHasAssistantMessages: (hasMessages: boolean) => void
  /**
   * External error message to display in the chat error banner (e.g., delete failure).
   */
  externalError?: string | null
  /**
   * Called to clear the external error (e.g., when the user sends a new message).
   */
  onClearExternalError?: () => void
  /**
   * Called when a variable is successfully added to the extraction.
   * Used to trigger a refresh of the extractions sidebar.
   */
  onExtractionAdded?: () => void
  /** Canonical assistant ID used by the UI and session/feedback services. */
  agent?: AgentName
}

/**
 * Core chat UI component.
 *
 * This is a thin view over the per-session {@link useChatSessions} store. All
 * message/streaming state lives in the store keyed by session id, so a response
 * that began in one session keeps streaming — and finishes — into that session
 * even after the user switches away, and is shown again on return. This
 * component only renders the active session's slice and forwards user actions.
 *
 * Header controls (new chat button, logout) are handled by the parent layout.
 *
 * @param sessionId              - The active session ID (controlled by parent).
 * @param isExistingSession      - Whether this session already exists in the backend.
 * @param onHasAssistantMessages - Notifies the parent when assistant messages appear/disappear.
 * @param externalError          - External error message to display in the error banner.
 * @param onClearExternalError   - Clears the external error (e.g., on new message send).
 * @param onExtractionAdded      - Refreshes the extractions side panel when a new variable has been added.
 * @param agent                  - Name of the agent for this chat.
 */
export default function ChatInterface({
  sessionId,
  isExistingSession,
  onHasAssistantMessages,
  externalError,
  onClearExternalError,
  onExtractionAdded,
  agent = DEFAULT_AGENT_NAME,
}: ChatInterfaceProps) {
  const auth = useAuth()
  const store = useChatSessions()

  // Generic collector for agent-specific, request-scoped invocation-body
  // fields, plus a generic "message sent" notification. This component knows
  // nothing about what any agent contributes or does on send; outside an
  // InvocationContextProvider both are inert no-ops.
  const invocationContext = useInvocationContext()

  // The active session's live slice. Re-reads on every store mutation because
  // the store bumps its context value identity on change.
  const view = store.getSession(sessionId)
  const { messages, error, loadingHistory, isStreaming, input } = view

  // Ref for message container to enable auto-scrolling.
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // The session the scroll effect last ran for. Used to detect a session
  // switch so the first scroll into a conversation snaps instantly instead of
  // animating.
  const scrolledSessionRef = useRef<string | null>(null)

  // Keep the latest loadHistory in a ref. The store's context value identity
  // changes on every stream tick (version bump), so depending on it directly
  // would re-run the load effect constantly; the ref keeps the effect scoped
  // to real session changes while always calling the current implementation.
  const loadHistoryRef = useRef(store.loadHistory)
  loadHistoryRef.current = store.loadHistory

  // When the active session changes, load its history if needed.
  useEffect(() => {
    if (!isExistingSession) return
    void loadHistoryRef.current(sessionId)
  }, [sessionId, isExistingSession])

  // Register default tool renderer (wildcard "*")
  useDefaultTool(({ name, args, status, result }) => (
    <ToolCallDisplay name={name} args={args} status={status} result={result} />
  ))

  // Register the describe_survey_variables tool renderer — adaptively shows
  // the single-variable card (DescribeVariableWidget) for 1 result, or the
  // compact grid table (DescribeVariablesGrid) for 2+ results.
  useWidgetToolRenderer("describe-survey-variable___describe_survey_variables", (props) => (
    <div>
      <ToolCallDisplay {...props} />
      {props.status === "complete" && props.result && (
        <DescribeVariablesRenderer result={props.result} sessionId={sessionId} onExtractionAdded={onExtractionAdded} />
      )}
    </div>
  ))

  // Register renderers for agent render tools (CountryList, SurveysOverview, etc.)
  useWidgetRenderers()

  // Auto-scroll to the newest content whenever the active session's messages
  // change.
  useEffect(() => {
    const isSessionSwitch = scrolledSessionRef.current !== sessionId
    scrolledSessionRef.current = sessionId
    if (messages.length === 0) return
    const behavior: ScrollBehavior = isStreaming && !isSessionSwitch ? "smooth" : "instant"
    messagesEndRef.current?.scrollIntoView({ behavior })
  }, [messages, sessionId, isStreaming])

  /**
   * Submit a user message for the active session via the store, clearing any
   * external (page-level) error first.
   *
   * @param userMessage - The user's prompt text.
   */
  const sendMessage = (userMessage: string): void => {
    if (!userMessage.trim()) return
    onClearExternalError?.()

    // Collect any agent-specific request-scoped fields (e.g. a collection
    // scope) and forward them opaquely. Empty for agents that contribute none.
    const extraBody = invocationContext.collect()
    void store.sendMessage(sessionId, userMessage, extraBody)
  }

  /**
   * Form submit handler for the chat input.
   *
   * @param e - The form submit event.
   */
  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    sendMessage(input)
  }

  /**
   * Update the active session's draft input text (preserved per session).
   *
   * @param next - The new draft text.
   */
  const handleSetInput = (next: string): void => {
    store.setInput(sessionId, next)
  }

  /**
   * Submit feedback for an assistant message in the active session.
   *
   * @param message        - The message being rated.
   * @param feedbackType   - "positive" or "negative".
   * @param comment        - Free-text comment from the user.
   * @param messageIndex   - Index of the message in the active session's array.
   * @param scope          - What the feedback targets (message/highlight/tool).
   * @param messageOverride - Alternate feedback text (highlight or tool summary).
   */
  const handleFeedbackSubmit = async (
    message: Message,
    feedbackType: "positive" | "negative",
    comment: string,
    messageIndex: number,
    scope: FeedbackScope,
    messageOverride?: string
  ): Promise<void> => {
    try {
      const idToken = await auth.getAccessToken()
      if (!idToken) {
        throw new Error("Authentication required. Please log in again.")
      }

      // Build simplified segments for the backend (tool call names/inputs/results + text)
      const segments = message.segments?.map(seg => {
        if (seg.type === "text") {
          return { type: "text" as const, content: seg.content }
        }
        return {
          type: "tool" as const,
          toolCall: {
            name: seg.toolCall.name,
            input: seg.toolCall.input,
            result: seg.toolCall.result,
          },
        }
      })

      // Find the preceding user message (walk backwards from this assistant message)
      let userMessage: string | undefined
      for (let i = messageIndex - 1; i >= 0; i--) {
        if (messages[i].role === "user") {
          userMessage = messages[i].content
          break
        }
      }

      await submitChatFeedback(
        {
          sessionId,
          message: messageOverride ?? message.content,
          feedbackType,
          userName: auth.user?.name || auth.user?.email || "Unknown",
          agent,
          userMessage,
          comment: comment || undefined,
          segments,
          feedbackScope: scope,
        },
        idToken
      )

      console.log("Feedback submitted successfully")
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error"
      console.error("Error submitting feedback:", err)
      store.setError(sessionId, `Failed to submit feedback: ${errorMessage}`)
    }
  }

  // The input is disabled while the active session is streaming or loading.
  const isBusy = isStreaming || loadingHistory

  // Initial (welcome) state: no messages and not loading history.
  const isInitialState = messages.length === 0 && !loadingHistory

  // Whether the active session has any assistant messages (drives New Chat button).
  const hasAssistantMessages = messages.some(message => message.role === "assistant")

  // Notify the parent layout whenever assistant-message presence changes.
  useEffect(() => {
    onHasAssistantMessages(hasAssistantMessages)
  }, [hasAssistantMessages, onHasAssistantMessages])

  // "How To Use" guide content for this agent, if one is registered. When
  // undefined, no help button is shown (agents opt in via howToUseContent.ts).
  const howToUseContent = getHowToUseContent(agent)

  return (
    <div className="relative flex flex-col h-full w-full">
      {howToUseContent && (
        <div className="absolute left-2 top-2 z-10">
          <HowToUseDialog
            content={howToUseContent}
            triggerClassName="bg-background/80 backdrop-blur-sm shadow-sm"
          />
        </div>
      )}

      {/* Error banner — shown below the shared AppHeader when a request fails */}
      {(error || externalError) && (
        <div className="flex-none">
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mx-4 mt-2">
            <p className="text-sm text-red-700">{error || externalError}</p>
          </div>
        </div>
      )}

      {isInitialState ? (
        <>
          <div className="grow" />

          <div className="text-center mb-6">
            <h1>{AGENT_WELCOME[agent].title} Assistant</h1>
            <p className="text-muted-foreground mt-2">{AGENT_WELCOME[agent].subtitle}</p>
          </div>

          <div className="lg:px-4 mb-4 max-w-4xl mx-auto w-full">
            <ChatInput
              input={input}
              setInput={handleSetInput}
              handleSubmit={handleSubmit}
              isLoading={isBusy}
            />
          </div>

          <div className="grow" />
        </>
      ) : (
        <>
          <div className="grow overflow-hidden">
            <div className="mx-auto w-full h-full">
              <ChatMessages
                messages={messages}
                messagesEndRef={messagesEndRef}
                sessionId={sessionId}
                loadingHistory={loadingHistory}
                onFeedbackSubmit={handleFeedbackSubmit}
              />
            </div>
          </div>

          <div className="flex-none">
            <div className="max-w-4xl mx-auto w-full">
              <ChatInput
                input={input}
                setInput={handleSetInput}
                handleSubmit={handleSubmit}
                isLoading={isBusy}
              />
            </div>
          </div>
        </>
      )}
    </div>
  )
}
